import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import type { AppDispatch, RootState } from "../../redux/store";

import {
  listenToPublishedProducts,
  filterLocalProductsByCountry,
  setFilters,
} from "../../redux/productSlice";
import { addToCart, decrementCartItem, clearCart } from "../../redux/cartSlice";
import { listenToBrandsByBusinessType } from "../../redux/commercePartnersSlice";
import { fetchAllProductSummaries } from "../../redux/productRatingSlice";
import { CurrencyContext } from "../../GLOBAL/CurrencyContext";
import appLogo from "../../assets/app/logo.png";

/* ═══════════════════════════════════════════════════════════
   CATEGORY SOURCE — strictly AutomotiveCategories, nothing else
═══════════════════════════════════════════════════════════ */
import {
  automotiveCategories,
  automotiveSubcategories,
  type CategoryItem,
} from "../../CATEGORIES/AutomotiveCategories";

/* ═══════════════════════════════════════════════════════════
   BUSINESS / STORE TYPE — pulled directly from the shared
   PartnerCategories registry (businessType / storeType), not
   hardcoded, so Automotive stays in sync with that source of
   truth — exactly the same pattern Kitchen / Farm / BeautyHome /
   BabyHome use.
   NOTE: verify "Automotive Store" is the exact name used in your
   PartnerCategories.ts — adjust the fallback id below if not.
═══════════════════════════════════════════════════════════ */
import { businessType, storeType } from "../../CATEGORIES/PartnerCategories";

import {
  Sizes,
  ShoeSizes,
  productColors,
} from "../../CATEGORIES/ATTRIBUTES/ProductsAttributes";

import {
  Search, ShoppingBag, LayoutGrid, X, ChevronRight, Plus, Minus,
  Heart, User, ShoppingCart, MapPin, Globe, AlertTriangle, ArrowRight,
  Package, Home, Wrench, Star, ChevronDown, Tag, Check,
} from "lucide-react";

/* ═══════════════════════════════════════════════════════════
   sphere helper — used for the colour-swatch variant picker
═══════════════════════════════════════════════════════════ */
function sphere3D(hex: string): string {
  return [
    `radial-gradient(circle at 35% 32%, rgba(255,255,255,0.6) 0%, rgba(255,255,255,0.12) 28%, transparent 52%),`,
    `radial-gradient(circle at 68% 72%, rgba(0,0,0,0.28) 0%, transparent 55%),`,
    `radial-gradient(circle at 50% 50%, ${hex} 0%, ${hex} 100%)`,
  ].join(" ");
}

/* ═══════════════════════════════════════════════════════════
   DESIGN TOKENS — strictly the Torque / Garagehaus palette
   (asphalt charcoal, safety-signal red-orange, caution amber,
   steel blue as the 3rd accent — an industrial, dashboard feel
   rather than the warm crimson/gold used on the baby vertical)
═══════════════════════════════════════════════════════════ */
const T = {
  pageBg:   "#F1F0EC",
  cardBg:   "#FFFFFF",
  headerBg: "#14181C",

  black:    "#14181C",
  white:    "#FFFFFF",
  offwhite: "#F1F0EC",
  muted:    "#5C6570",
  mutedDim: "#8B95A1",

  orange:   "#FFB020", // caution amber
  red:      "#E8491D", // safety-signal red-orange

  blue:     "#2E6F9E", // steel blue — used as the 3rd accent in rotations
  blueDim:  "rgba(46,111,158,0.10)",

  cream:    "#EFEDE8",
  peach:    "#E4C9A6",

  border:       "#E2DFD8",
  borderStrong: "rgba(20,24,28,0.24)",

  grad:  "linear-gradient(135deg,#1E252B 0%,#0D1114 100%)",   // charcoal — used for the header shell
  gradH: "linear-gradient(90deg,#FFB020 0%,#E8491D 100%)",     // amber → red-orange — the "signal" accent

  outline: "1.5px solid #E2DFD8",
};

const GRAD   = T.grad;
const GRAD_H = T.gradH;

const RGBA_DARK = (a: number) => `rgba(20,24,28,${a})`;
const RGBA_GOLD = (a: number) => `rgba(255,176,32,${a})`;
const RGBA_RED  = (a: number) => `rgba(232,73,29,${a})`;

/* amber / red-orange / steel-blue accent rotation — keeps the page on-brand */
const PRODUCT_ACCENT_COLORS = [T.orange, T.red, T.blue];
const productAccentColor = (key: string) => {
  let hash = 0;
  for (let i = 0; i < key.length; i++) hash = (hash * 31 + key.charCodeAt(i)) >>> 0;
  return PRODUCT_ACCENT_COLORS[hash % PRODUCT_ACCENT_COLORS.length];
};

/* ═══════════════════════════════════════════════════════════
   ATTRIBUTE LOOKUPS (flattened for the variant modal)
═══════════════════════════════════════════════════════════ */
const colorPalette = productColors[0]?.values ?? [];
const sizePalette   = [...Sizes, ...ShoeSizes];

/* ═══════════════════════════════════════════════════════════
   CATEGORY REGISTRY — strictly AutomotiveCategories
   Each top-level category maps to its own subcategory list,
   derived directly from automotiveSubcategories' parentId —
   no manual per-category array to keep in sync. Same one-layer
   shape Kitchen/BeautyHome/BabyHome use for CAT_SUBCATS.
═══════════════════════════════════════════════════════════ */
const ALL_TOP_CATS: CategoryItem[] = automotiveCategories;

const CAT_SUBCATS: Record<number, CategoryItem[]> = automotiveCategories.reduce(
  (acc, cat) => {
    acc[cat.id] = automotiveSubcategories.filter((s) => s.parentId === cat.id);
    return acc;
  },
  {} as Record<number, CategoryItem[]>
);

/* ═══════════════════════════════════════════════════════════
   BUSINESS / STORE TYPE — Automotive isn't its own top-level
   business type. It lives under business type "Stores", store
   type "Automotive Store" — resolved from the shared
   PartnerCategories registry rather than hardcoded, so every
   partner query on this page — local AND global — matches BOTH
   ids, or the grid will pull in non-Automotive-Store brands.
   Same exact pattern as Kitchen ("Kitchen Store") / Farm
   ("Farm Store") / BeautyHome ("Beauty Store") / BabyHome
   ("Baby Store").
═══════════════════════════════════════════════════════════ */
const BUSINESS_TYPE_ID = businessType.find((b) => b.name === "Stores")?.id ?? 12;
const STORE_TYPE_ID    = storeType.find((s) => s.name === "Automotive Store")?.id ?? 1216;

const COUNTRY_TO_CONTINENT: Record<string, string> = {
  estonia: "Europe", latvia: "Europe", lithuania: "Europe", finland: "Europe",
  sweden: "Europe", norway: "Europe", denmark: "Europe", germany: "Europe",
  france: "Europe", spain: "Europe", italy: "Europe", portugal: "Europe",
  poland: "Europe", "united kingdom": "Europe", ireland: "Europe",
  netherlands: "Europe", belgium: "Europe", austria: "Europe",
  switzerland: "Europe", greece: "Europe",
  "united states": "North America", canada: "North America", mexico: "North America",
  brazil: "South America", argentina: "South America",
  nigeria: "Africa", kenya: "Africa", "south africa": "Africa",
  china: "Asia", japan: "Asia", india: "Asia", "united arab emirates": "Asia",
  australia: "Oceania", "new zealand": "Oceania",
};
const getContinent = (country?: string | null): string | null => {
  if (!country) return null;
  return COUNTRY_TO_CONTINENT[country.toLowerCase().trim()] ?? null;
};

/* tier 0 = same city, 1 = same country, 2 = same continent, 3 = rest of world */
const isLocalTier = (tier: number) => tier <= 1;
const isGlobalTier = (tier: number) => tier >= 2;

/* ═══════════════════════════════════════════════════════════
   GLOBAL CSS
═══════════════════════════════════════════════════════════ */
const GLOBAL_CSS = `
@import url('https://fonts.googleapis.com/css2?family=Rajdhani:wght@500;600;700&family=Inter:wght@300;400;500;600;700;800;900&display=swap');
*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
::-webkit-scrollbar { display: none; }
input, button { font-family: inherit; }
input::placeholder { color: rgba(255,255,255,.55); }

@keyframes gradSpin { 0%{background-position:0% 50%;} 50%{background-position:100% 50%;} 100%{background-position:0% 50%;} }
@keyframes mc-pulse   { 0%,100%{opacity:1} 50%{opacity:.35} }

.p-card { transition: box-shadow .22s, transform .22s; }
.p-card:hover { box-shadow: 0 0 0 1.5px ${RGBA_RED(0.30)}, 0 14px 36px ${RGBA_GOLD(0.18)}; transform: translateY(-6px); }

.add-btn-3d { transition: transform .18s ease, box-shadow .18s ease; }
.add-btn-3d:hover  { transform: translateY(-3px); box-shadow: 0 10px 24px var(--shadow) !important; }
.add-btn-3d:active { transform: translateY(0) scale(.96); box-shadow: 0 4px 14px var(--shadow) !important; }

.h-btn{
  display:flex;align-items:center;gap:6px;
  background:rgba(255,255,255,.12);
  border:1px solid rgba(255,255,255,.22);
  cursor:pointer;color:#fff;
  font-size:12px;font-weight:700;
  padding:7px 13px;border-radius:10px;
  flex-shrink:0;white-space:nowrap;
  transition:background .2s;
  font-family:inherit;
}
.h-btn:hover{background:rgba(255,255,255,.20);}

.nav-item { display:flex; align-items:center; gap:12px; padding:13px 18px; cursor:pointer; font-size:14px; font-weight:700; color:#F1F0EC; background:none; width:100%; text-align:left; border:none; border-bottom:1px solid rgba(255,255,255,.10); transition:background .15s; }
.nav-item:hover { background: rgba(255,255,255,.08); }

.tab-item { display:flex; flex-direction:column; align-items:center; gap:3px; font-size:10px; font-weight:700; cursor:pointer; color:#9AA3AD; padding:4px 10px; transition:color .2s; }
.tab-item.active { color:#E8491D; }

.gauge-label { font-family:'Rajdhani',sans-serif; letter-spacing:0.14em; text-transform:uppercase; }
`;

/* ═══════════════════════════════════════════════════════════
   SIDEBAR STYLES — single expandable top-level category cards
═══════════════════════════════════════════════════════════ */
const A: Record<string, React.CSSProperties> = {
  commerceCard:   { borderRadius: 14, padding: 18, background: "#fff", marginBottom: 16, boxShadow: `0 2px 16px ${RGBA_DARK(0.06)}`, border: T.outline },
  commerceHeader: { fontSize: 16, fontWeight: 900, display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer", color: T.black },
  row:            { display: "flex", alignItems: "center", gap: 10, fontWeight: 700, color: T.black },
  sub:            { paddingLeft: 22, paddingTop: 8 },
  productGroup:   { paddingLeft: 20, paddingTop: 7, paddingBottom: 4, fontWeight: 600, color: T.mutedDim, cursor: "pointer", fontSize: 13, display: "flex", alignItems: "center", gap: 8 },
  sidebar:        { background: "rgba(255,255,255,.98)", borderRadius: 16, padding: 20, boxShadow: `0 4px 24px ${RGBA_DARK(0.08)}`, position: "sticky" as const, top: 86, maxHeight: "calc(100vh - 100px)", overflowY: "auto" as const, border: T.outline },
};

/* page layout styles */
const S: Record<string, React.CSSProperties> = {
  page:        { minHeight: "100vh", background: T.pageBg, paddingBottom: 90, fontFamily: "'Inter',sans-serif", position: "relative" as const },
  content:     { position: "relative" as const, zIndex: 1 },
  headerTop:   { background: T.headerBg, backgroundImage: T.grad, position: "sticky", top: 0, zIndex: 200, boxShadow: `0 4px 24px ${RGBA_DARK(0.35)}`, borderBottom: `2px solid ${T.red}` },
  headerRow:   { display: "flex", alignItems: "center", gap: 10, padding: "14px 16px" },
  searchWrap:  { display: "flex", alignItems: "center", flex: 1, background: "rgba(255,255,255,.10)", borderRadius: 10, overflow: "hidden", border: "1.5px solid rgba(255,255,255,.22)", transition: "border-color .2s,box-shadow .2s" },
  searchInput: { flex: 1, border: "none", outline: "none", padding: "10px 16px", fontSize: 14, color: "#fff", background: "transparent", fontFamily: "inherit" },
  searchBtn:   { width: 44, minHeight: 42, background: "rgba(255,255,255,.16)", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, borderRadius: "0 10px 10px 0" },
  desktopGrid: { display: "grid", gap: 24, padding: "24px 24px 0" },
  grid:        { display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(240px,1fr))", gap: 20 },
  mobileGrid:  { display: "grid", gridTemplateColumns: "repeat(2,1fr)", gap: 12 },
  mobileTabs:  { position: "fixed" as const, bottom: 0, left: 0, right: 0, height: 64, background: "#fff", display: "flex", justifyContent: "space-around", alignItems: "center", boxShadow: `0 -4px 24px ${RGBA_DARK(0.10)}`, zIndex: 100, borderTop: `1px solid ${T.border}` },
};

/* ═══════════════════════════════════════════════════════════
   PAGE WATERMARK — faded logo tiled across the concrete background
═══════════════════════════════════════════════════════════ */
const PageWatermark: React.FC = () => (
  <div
    aria-hidden="true"
    style={{
      position: "fixed",
      inset: 0,
      zIndex: 0,
      pointerEvents: "none",
      backgroundImage: `url(${appLogo})`,
      backgroundRepeat: "repeat",
      backgroundSize: "170px 170px",
      opacity: 0.04,
      filter: "grayscale(1)",
    }}
  />
);

/* ═══════════════════════════════════════════════════════════
   MAIN COMPONENT
═══════════════════════════════════════════════════════════ */
const AutomotiveHome: React.FC = () => {
  const dispatch  = useDispatch<AppDispatch>();
  const navigate  = useNavigate();
  const { rate, symbol } = useContext(CurrencyContext);

  const authUser   = useSelector((s: RootState) => s.auth.user);
  const authLoaded = useSelector((s: RootState) => s.auth.authLoaded);
  const userId     = authUser?.userId ?? "";

  const listings         = useSelector((s: RootState) => s.products.products);
  const filteredListings = useSelector((s: RootState) => (s as any).products.filteredProducts);
  const cartItems        = useSelector((s: RootState) => s.cart.items);
  const cartTotal         = cartItems.reduce((s: number, i: any) => s + i.qty, 0);
  const ratingSummaries   = useSelector((s: RootState) => (s as any).productRatings?.summaries ?? {});

  const commercePartners = useSelector((s: RootState) => s.commercePartners.partners);

  const [isMobile,        setIsMobile]        = useState(window.innerWidth <= 768);
  const [catOpen,         setCatOpen]         = useState(false);
  const [desktopCatOpen,  setDesktopCatOpen]  = useState(false);
  const [searchQuery,     setSearchQuery]     = useState("");
  const [searchFocused,   setSearchFocused]   = useState(false);

  const [activeCat,           setActiveCat]           = useState<number | null>(null);
  const [activeProductGroup,  setActiveProductGroup]  = useState<number | null>(null);

  const [openCategory,    setOpenCategory]    = useState<string | null>(null);
  const [shopCatExpanded, setShopCatExpanded] = useState(true);

  const [userCountry,       setUserCountry]       = useState<string | null>(null);
  const [userCity,          setUserCity]          = useState<string | null>(null);
  const [userLat,           setUserLat]           = useState<number | null>(null);
  const [userLng,           setUserLng]           = useState<number | null>(null);
  const [locationDenied,    setLocationDenied]    = useState(false);
  const [locationLoading,   setLocationLoading]   = useState(true);
  const [locationModalOpen, setLocationModalOpen] = useState(false);
  const [showGlobal,        setShowGlobal]        = useState(false);
  const locationActive = !locationDenied && userLat !== null;

  const [selColor, setSelColor] = useState<number | null>(null);
  const [selSize,  setSelSize]  = useState<number | null>(null);
  const [qty,      setQty]      = useState(1);
  const [variantOpen, setVariantOpen] = useState(false);
  const [variantItem, setVariantItem] = useState<any | null>(null);

  const [wished, setWished] = useState<Set<string>>(new Set());

  /* ═══════════════════════════════════════════════════════
     AUTOMOTIVE-ONLY PARTNER SET — businessType "Stores" AND
     storeType "Automotive Store". This is the strict filter:
     everything downstream (brand rows, tier ranking, "only
     automotive brands" even in Global mode) derives from this
     set, not from all commercePartners. Same pattern as
     Kitchen / Farm / BeautyHome / BabyHome.
  ═══════════════════════════════════════════════════════════ */
  const partners = useMemo(() => {
    return (commercePartners || []).filter(
      (p: any) => p.categoryId === BUSINESS_TYPE_ID && p.storeTypeId === STORE_TYPE_ID
    );
  }, [commercePartners]);

  const autoBrandIds = useMemo(() => new Set(partners.map((p: any) => p.brandId)), [partners]);

  const brandMap = useMemo(() => {
    const m = new Map<string, any>();
    partners.forEach((p: any) => m.set(p.brandId, p));
    return m;
  }, [partners]);

  const brandTier = useMemo(() => {
    const tiers = new Map<string, number>();
    const deviceCity = userCity?.toLowerCase().trim();
    const deviceCountry = userCountry?.toLowerCase().trim();
    const deviceContinent = getContinent(userCountry);
    partners.forEach((p: any) => {
      const brandCity = p.address?.city?.toLowerCase().trim();
      const brandCountry = p.address?.country?.toLowerCase().trim();
      const brandContinent = getContinent(p.address?.country);
      let tier = 3;
      if (deviceCity && brandCity && brandCity === deviceCity) tier = 0;
      else if (deviceCountry && brandCountry && brandCountry === deviceCountry) tier = 1;
      else if (deviceContinent && brandContinent && brandContinent === deviceContinent) tier = 2;
      tiers.set(p.brandId, tier);
    });
    return tiers;
  }, [partners, userCity, userCountry]);

  /* ── responsive ── */
  useEffect(() => {
    const fn = () => setIsMobile(window.innerWidth <= 768);
    fn(); window.addEventListener("resize", fn);
    return () => window.removeEventListener("resize", fn);
  }, []);

  /* ── auth gate ── */
  useEffect(() => {
    if (!authLoaded) return;
    if (!authUser || !userId) navigate("/AuthSwitch");
  }, [authLoaded, authUser, userId, navigate]);

  /* ── data listeners/slices ── */
  useEffect(() => { dispatch(listenToPublishedProducts()); }, [dispatch]);
  useEffect(() => { dispatch(fetchAllProductSummaries()); }, [dispatch]);
  useEffect(() => {
    const unsub = dispatch(listenToBrandsByBusinessType(BUSINESS_TYPE_ID) as any);
    return () => { if (typeof unsub === "function") unsub(); };
  }, [dispatch]);
  useEffect(() => { dispatch(filterLocalProductsByCountry(null) as any); }, [dispatch]);

  /* ── geolocation ── */
  useEffect(() => {
    if (!navigator.geolocation) {
      setLocationDenied(true); setLocationLoading(false);
      dispatch(filterLocalProductsByCountry(null) as any);
      return;
    }
    const detect = () => {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          setUserLat(pos.coords.latitude); setUserLng(pos.coords.longitude); setLocationDenied(false);
          if ((window as any).google?.maps) {
            const gc = new (window as any).google.maps.Geocoder();
            gc.geocode({ location: { lat: pos.coords.latitude, lng: pos.coords.longitude } }, (results: any, status: string) => {
              if (status === "OK" && results?.[0]) {
                const comps = results[0].address_components;
                const get   = (t: string) => comps.find((c: any) => c.types.includes(t))?.long_name ?? null;
                setUserCity(get("locality") || get("administrative_area_level_2"));
                const country = get("country"); setUserCountry(country);
                if (country) dispatch(filterLocalProductsByCountry(country) as any);
              }
              setLocationLoading(false);
            });
          } else { setLocationLoading(false); }
        },
        (err) => {
          if (err.code === err.PERMISSION_DENIED) setLocationDenied(true);
          setLocationLoading(false); dispatch(filterLocalProductsByCountry(null) as any);
        }
      );
    };
    detect();
    if ("permissions" in navigator) {
      navigator.permissions.query({ name: "geolocation" as PermissionName }).then(r => {
        r.onchange = () => { if (r.state === "granted") detect(); };
      });
    }
  }, [dispatch]);

  useEffect(() => {
    if (!userCountry) return;
    dispatch(filterLocalProductsByCountry(showGlobal ? null : userCountry) as any);
  }, [userCountry, showGlobal, dispatch]);

  const handleToggleGlobal = () => {
    const next = !showGlobal; setShowGlobal(next);
    dispatch(filterLocalProductsByCountry(next ? null : userCountry) as any);
  };

  useEffect(() => { if (searchQuery.trim()) setActiveProductGroup(null); }, [searchQuery]);

  const closePanel = () => { setCatOpen(false); setDesktopCatOpen(false); };

  const resetAllFilters = () => {
    setActiveCat(null); setActiveProductGroup(null);
    dispatch(setFilters({ city: null, country: null }));
    dispatch(filterLocalProductsByCountry(showGlobal ? null : userCountry) as any);
  };

  /* Top-level category tap → FILTERS in place (toggle), never navigates.
     Same behaviour as Kitchen / Farm's handleCatSelect. */
  const handleCatSelect = (catId: number) => {
    setActiveCat(prev => {
      const next = prev === catId ? null : catId;
      if (next === null) setActiveProductGroup(null);
      return next;
    });
  };

  const handleProductGroupSelect = (catId: number, groupId: number) => {
    setActiveCat(catId);
    setActiveProductGroup(prev => (prev === groupId ? null : groupId));
    closePanel();
  };

  /* ── cart — single-brand cart, same actions as Kitchen ── */
  const getQty = (id: string) => cartItems.find((i: any) => i.listingId === id)?.qty ?? 0;

  const SINGLE_BRAND_CART_MODE = true;
  const ensureCartBrandCompat = (newBrandId?: string) => {
    if (!SINGLE_BRAND_CART_MODE || !cartItems.length) return;
    const currentBrandId = cartItems[0]?.brandId;
    if (currentBrandId && newBrandId && currentBrandId !== newBrandId) dispatch(clearCart());
  };

  const getVariantMode = (item: any): "color" | null => {
    if (!item || !item.variants || !Object.keys(item.variants).length) return null;
    return "color";
  };
  const canContinue = getVariantMode(variantItem) === null || (selColor !== null && qty > 0);

  const openProductModal = (item: any) => {
    setVariantItem(item); setSelColor(null); setSelSize(null); setQty(1); setVariantOpen(true);
  };

  const handleAddToCart = (item: any, e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (getVariantMode(item) !== null) { openProductModal(item); return; }
    ensureCartBrandCompat(item.brandId);
    dispatch(addToCart({
      listingId: item.listingId, name: item.name!, rate: item.rate!,
      photo: item.photos?.[0], hostId: item.hostId, brandId: item.brandId,
    } as any));
  };

  const handleVariantContinue = (toCheckout: boolean) => {
    if (!variantItem) return;
    ensureCartBrandCompat(variantItem.brandId);
    dispatch(addToCart({
      listingId: variantItem.listingId, name: variantItem.name!,
      rate: variantItem.rate!, qty,
      photo: variantItem.variants?.[selColor!]?.imageUrl ?? variantItem.photos?.[0],
      colorId: selColor ?? undefined, sizeId: selSize ?? undefined,
      hostId: variantItem.hostId, brandId: variantItem.brandId,
    } as any));
    setVariantOpen(false); setVariantItem(null); setSelColor(null); setSelSize(null); setQty(1);
    if (toCheckout) goToCheckout();
  };

  const toggleWish = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setWished(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  };

  const goToDetails  = (id: string) => navigate(`/autopartdetails/${id}`);
  const goToCheckout = () => navigate(`/autocheckout/${userId}`, { state: { items: cartItems } });
  const goToBrand    = (id: string) => navigate(`/StoreHome/${id}`);

  /* ── filtered products — category/group + search + STRICT auto-
     brand scoping. autoBrandIds is derived from businessType
     "Stores" / storeType "Automotive Store" partners, so even in
     Global mode only Automotive Store brands can ever appear here. ── */
  const filteredProducts = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    let list: any[] = (filteredListings ?? listings).filter((l: any) => {
      if (l.isPublished === false) return false;
      if (!autoBrandIds.has(l.brandId)) return false;
      if (activeCat) {
        const matchesCat = l.categoryId === activeCat || l.subcategoryId === activeCat || l.productId === activeCat;
        if (!matchesCat) return false;
      }
      if (activeProductGroup && l.subcategoryId !== activeProductGroup && l.productId !== activeProductGroup && l.productVariantId !== activeProductGroup) return false;
      if (q && !`${l.name ?? ""} ${l.description ?? ""}`.toLowerCase().includes(q)) return false;
      return true;
    });

    if (locationActive) {
      list = list.filter((l: any) => {
        const tier = brandTier.get(l.brandId) ?? 3;
        return showGlobal ? isGlobalTier(tier) : isLocalTier(tier);
      });
    }
    return list;
  }, [filteredListings, listings, autoBrandIds, activeCat, activeProductGroup, searchQuery, showGlobal, brandTier, locationActive]);

  const productsByBrand = useMemo(() => {
    const order: string[] = [];
    const map = new Map<string, any[]>();
    filteredProducts.forEach((item: any) => {
      const bId = item.brandId || "unknown";
      if (!map.has(bId)) { map.set(bId, []); order.push(bId); }
      map.get(bId)!.push(item);
    });
    let groups = order.map(bId => ({ brandId: bId, items: map.get(bId)! }));
    groups = [...groups].sort((a, b) => (brandTier.get(a.brandId) ?? 3) - (brandTier.get(b.brandId) ?? 3));
    return groups;
  }, [filteredProducts, brandTier]);

  const activeCatName = activeCat ? ALL_TOP_CATS.find(c => c.id === activeCat)?.name ?? null : null;

  /* ═══════════════════════════════════
     HEADER — location · search · categories · cart. Nothing else.
  ═══════════════════════════════════ */
  const HeaderRow = () => (
    <div style={S.headerRow}>
      {locationLoading ? (
        <div className="h-btn">
          <div style={{ width: 6, height: 6, borderRadius: "50%", background: "#fff", animation: "mc-pulse 1.2s infinite" }} />
          <span style={{ fontSize: 11, color: "#fff" }}>Locating…</span>
        </div>
      ) : locationDenied ? (
        <button className="h-btn" onClick={() => setLocationModalOpen(true)}><AlertTriangle size={14} /></button>
      ) : (
        <button onClick={handleToggleGlobal} className="h-btn" style={{ background: showGlobal ? "rgba(255,255,255,.06)" : "rgba(255,255,255,.16)" }}>
          {showGlobal ? <Globe size={13} /> : <MapPin size={13} />}
          <span style={{ display: isMobile ? "none" : "inline" }}>{showGlobal ? "Global" : userCity || userCountry || "Nearby"}</span>
        </button>
      )}

      <div style={{ ...S.searchWrap, borderColor: searchFocused ? T.orange : "rgba(255,255,255,.22)" }}>
        <input
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          onFocus={() => setSearchFocused(true)}
          onBlur={() => setSearchFocused(false)}
          placeholder="Search parts, tools, gear…"
          style={S.searchInput}
        />
        {searchQuery && (
          <button onClick={() => setSearchQuery("")} style={{ background: "none", border: "none", cursor: "pointer", padding: "0 8px", color: "#fff" }}>
            <X size={14} />
          </button>
        )}
        <button style={S.searchBtn}><Search size={17} color="#fff" /></button>
      </div>

      <button onClick={() => isMobile ? setCatOpen(true) : setDesktopCatOpen(v => !v)} className="h-btn">
        <LayoutGrid size={14} /><span style={{ display: isMobile ? "none" : "inline" }}>Categories</span>
      </button>

      <button onClick={goToCheckout} className="h-btn" style={{ position: "relative", background: cartTotal > 0 ? "rgba(255,176,32,.22)" : "rgba(255,255,255,.12)" }}>
        <ShoppingCart size={18} />
        {!isMobile && <span>{cartTotal > 0 ? `${cartTotal} items` : "Cart"}</span>}
        {cartTotal > 0 && (
          <span style={{ position: "absolute", top: -4, right: -4, background: T.red, color: "#fff", fontSize: 9, fontWeight: 900, width: 17, height: 17, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center" }}>
            {cartTotal > 99 ? "99+" : cartTotal}
          </span>
        )}
      </button>
    </div>
  );

  /* ═══════════════════════════════════════════════════════════
     SIDEBAR TREE — SINGLE top-level expandable category cards,
     each expanding to its own subcategory list. Tapping a
     subcategory FILTERS (never navigates).
  ═══════════════════════════════════════════════════════════ */
  const CategoryTree: React.FC<{ category: CategoryItem }> = ({ category }) => {
    const isOpen = openCategory === category.name;
    const subcats = CAT_SUBCATS[category.id] ?? [];
    const isCatSelected = activeCat === category.id && activeProductGroup === null;
    return (
      <div style={A.commerceCard}>
        <div style={{ ...A.commerceHeader, color: isCatSelected ? T.red : T.black }} onClick={() => setOpenCategory(isOpen ? null : category.name)}>
          <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {category.name}
            {isCatSelected && <Check size={14} color={T.red} />}
          </span>
          <motion.div animate={{ rotate: isOpen ? 90 : 0 }}><ChevronRight size={16} /></motion.div>
        </div>
        <AnimatePresence>
          {isOpen && (
            <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}>
              <div style={A.sub}>
                {subcats.map((p) => {
                  const sel = activeProductGroup === p.id;
                  return (
                    <div key={p.id}
                      style={{ ...A.productGroup, color: sel ? T.red : T.mutedDim, fontWeight: sel ? 800 : 600 }}
                      onClick={() => handleProductGroupSelect(category.id, p.id)}>
                      {p.name}
                      {sel && <Check size={12} color={T.red} style={{ marginLeft: "auto" }} />}
                    </div>
                  );
                })}
                {subcats.length === 0 && (
                  <div style={{ ...A.productGroup, color: "#B7BEC6", cursor: "default" }}>No items yet</div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  };

  const CategoryPanelContent = () => (
    <>
      {ALL_TOP_CATS.map((cat) => <CategoryTree key={cat.id} category={cat} />)}
    </>
  );

  const MobileCategorySheet = () => (
    <AnimatePresence>
      {isMobile && catOpen && (
        <>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setCatOpen(false)} style={{ position: "fixed", inset: 0, background: RGBA_DARK(0.5), zIndex: 400 }} />
          <motion.div initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }} transition={{ type: "spring", stiffness: 340, damping: 30 }}
            style={{ position: "fixed", bottom: 0, left: 0, right: 0, maxHeight: "82vh", borderRadius: "18px 18px 0 0", background: "#fff", zIndex: 401, border: T.outline, overflowY: "auto" }}>
            <div style={{ width: 36, height: 4, background: RGBA_DARK(0.16), borderRadius: 2, margin: "14px auto 0" }} />
            <div style={{ display: "flex", alignItems: "center", borderBottom: `1px solid ${T.border}`, padding: "12px 20px 10px" }}>
              <span className="gauge-label" style={{ fontWeight: 800, fontSize: 11, color: T.red }}>CATEGORIES</span>
              <div style={{ flex: 1 }} />
              <button onClick={() => setCatOpen(false)} style={{ background: "none", border: "none", cursor: "pointer" }}><X size={15} color={T.muted} /></button>
            </div>
            <div style={{ padding: "16px 16px 32px" }}><CategoryPanelContent /></div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );

  /* ═══════════════════════════════════
     CATEGORY ICON BAR — kept exactly as-is (scrollable), now
     wired to the in-place filter instead of navigation.
  ═══════════════════════════════════ */
  const CategoryIconBar = () => (
    <div style={{ marginBottom: 22, padding: isMobile ? "14px 16px" : "18px 24px", background: T.headerBg, backgroundImage: T.grad, borderRadius: 14, boxShadow: `0 8px 24px ${RGBA_DARK(0.28)}`, borderBottom: `2px solid ${T.orange}` }}>
      <div style={{ display: "flex", gap: isMobile ? 14 : 22, overflowX: "auto", scrollbarWidth: "none" as any, WebkitOverflowScrolling: "touch" as any, paddingBottom: 2 }}>
        {ALL_TOP_CATS.map((cat, i) => {
          const isSel = activeCat === cat.id;
          const color = cat.color ?? [T.orange, T.red, T.blue][i % 3];
          const Icon = cat.icon;
          return (
            <button key={cat.id}
              onClick={() => handleCatSelect(cat.id)}
              style={{ background: "none", border: "none", cursor: "pointer", flexShrink: 0, display: "flex", flexDirection: "column", alignItems: "center", gap: 6, padding: "2px 4px" }}>
              <div style={{
                width: isMobile ? 46 : 54, height: isMobile ? 46 : 54, borderRadius: 12, overflow: "hidden",
                border: `2px solid ${isSel ? T.orange : "rgba(255,255,255,.30)"}`,
                boxShadow: isSel ? `0 0 0 3px rgba(255,176,32,.25)` : "none", transition: "all .2s",
              }}>
                {cat.image ? (
                  <img src={cat.image} alt={cat.name} style={{ width: "100%", height: "100%", objectFit: "cover", opacity: isSel ? 1 : 0.85, transition: "opacity .2s" }} />
                ) : (
                  <div style={{ width: "100%", height: "100%", background: color, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <Icon size={isMobile ? 20 : 24} color="#fff" />
                  </div>
                )}
              </div>
              <span style={{ fontSize: 10, fontWeight: isSel ? 800 : 600, color: "#fff", whiteSpace: "nowrap" }}>{cat.name}</span>
            </button>
          );
        })}
      </div>
    </div>
  );

  /* ═══════════════════════════════════
     SHOP BY CATEGORY — group cards, strictly automotiveCategories,
     tiles SELECT a filter (handleCatSelect), no navigation.
  ═══════════════════════════════════ */
  const ShopByCategory = () => {
    const chunk = (arr: CategoryItem[], title: string) => {
      const chunks: CategoryItem[][] = [];
      for (let i = 0; i < arr.length; i += 4) chunks.push(arr.slice(i, i + 4));
      return chunks.map((items, idx) => ({
        title: chunks.length > 1 ? `${title} ${idx + 1}` : title,
        items,
      }));
    };

    const cardGroups = chunk(ALL_TOP_CATS, "Auto Parts & Accessories");

    return (
      <div style={{ marginBottom: 28 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14, gap: 12 }}>
          <div onClick={() => setShopCatExpanded(v => !v)} style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", userSelect: "none" }}>
            <span style={{ fontWeight: 900, fontSize: isMobile ? 16 : 18, color: T.black, letterSpacing: -0.3 }}>
              Shop by Category
            </span>
            <motion.div animate={{ rotate: shopCatExpanded ? 0 : -90 }} transition={{ duration: 0.2 }}>
              <ChevronDown size={20} color={T.muted} />
            </motion.div>
          </div>
        </div>

        <AnimatePresence initial={false}>
          {shopCatExpanded && (
            <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.25 }} style={{ overflow: "hidden" }}>
              <div style={isMobile
                ? { display: "flex", gap: 14, overflowX: "auto", scrollSnapType: "x mandatory", WebkitOverflowScrolling: "touch" as any, scrollbarWidth: "none" as any, paddingBottom: 4 }
                : { display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(220px,1fr))", gap: 18 }
              }>
                {cardGroups.map((group) => (
                  <div key={group.title}
                    style={{ flexShrink: 0, ...(isMobile ? { width: "78vw", scrollSnapAlign: "start" } : {}), background: "#fff", borderRadius: 16, overflow: "hidden", border: T.outline, boxShadow: `0 6px 28px ${RGBA_DARK(0.06)}`, transition: "box-shadow .22s, border-color .22s, transform .22s" }}
                    onMouseEnter={e => { const el = e.currentTarget as HTMLElement; el.style.boxShadow = `0 8px 0 ${RGBA_RED(0.28)}, 0 16px 38px ${RGBA_DARK(0.16)}`; el.style.borderColor = T.red; el.style.transform = "translateY(-4px)"; }}
                    onMouseLeave={e => { const el = e.currentTarget as HTMLElement; el.style.boxShadow = `0 6px 28px ${RGBA_DARK(0.06)}`; el.style.borderColor = T.border; el.style.transform = "translateY(0)"; }}
                  >
                    <div style={{ padding: isMobile ? "10px 12px 6px" : "14px 16px 8px", fontWeight: 900, fontSize: isMobile ? 12 : 14, color: T.black, letterSpacing: -.2 }}>{group.title}</div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: isMobile ? 4 : 8, padding: isMobile ? "0 8px 8px" : "0 10px 10px" }}>
                      {group.items.map((cat) => {
                        const Icon = cat.icon;
                        const sel = activeCat === cat.id;
                        return (
                          <button key={cat.id} onClick={() => handleCatSelect(cat.id)} style={{ background: "none", border: "none", cursor: "pointer", padding: 0, borderRadius: isMobile ? 10 : 12, overflow: "hidden" }}>
                            <div style={{ width: "100%", aspectRatio: "1/1", borderRadius: isMobile ? 10 : 12, overflow: "hidden", position: "relative", background: T.cream, outline: sel ? `2.5px solid ${T.red}` : "none", outlineOffset: 2 }}>
                              {cat.image ? (
                                <>
                                  <img src={cat.image} alt={cat.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                                  <div style={{ position: "absolute", inset: 0, background: `linear-gradient(to top,${RGBA_DARK(0.75)},transparent 50%)` }} />
                                  <span style={{ position: "absolute", bottom: isMobile ? 4 : 6, left: 0, right: 0, textAlign: "center", fontSize: isMobile ? 9 : 11, fontWeight: 800, color: "#fff", lineHeight: 1.2, textShadow: "0 1px 4px rgba(0,0,0,.5)" }}>
                                    {cat.name}
                                  </span>
                                </>
                              ) : (
                                <div style={{ width: "100%", height: "100%", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 6 }}>
                                  <Icon size={26} color={T.mutedDim} />
                                  <span style={{ fontSize: isMobile ? 9 : 11, fontWeight: 800, color: T.black, textAlign: "center", padding: "0 4px" }}>{cat.name}</span>
                                </div>
                              )}
                              {sel && (
                                <div style={{ position: "absolute", top: 5, right: 5, width: 18, height: 18, borderRadius: "50%", background: T.red, display: "flex", alignItems: "center", justifyContent: "center" }}>
                                  <Check size={11} color="#fff" strokeWidth={3} />
                                </div>
                              )}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                    <button onClick={() => handleCatSelect(group.items[0].id)}
                      style={{ width: "100%", padding: isMobile ? "7px 12px" : "9px 16px", background: "none", border: "none", borderTop: `1.5px solid ${T.border}`, cursor: "pointer", fontSize: isMobile ? 11 : 13, fontWeight: 700, color: T.red, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                      See all<ArrowRight size={isMobile ? 12 : 14} />
                    </button>
                  </div>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  };

  /* ═══════════════════════════════════
     BRAND ROW HEADER — clicking navigates to the store via brandId
  ═══════════════════════════════════ */
  const BrandRowHeader = ({ brandId }: { brandId: string }) => {
    const partner  = brandMap.get(brandId);
    const shopName = partner?.name || "Garage";
    const shopLogo = partner?.image || appLogo;
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 10, margin: "26px 0 12px" }}>
        <button onClick={() => goToBrand(brandId)} style={{ background: "none", cursor: "pointer", width: 34, height: 34, borderRadius: 8, overflow: "hidden", flexShrink: 0, border: `1px solid ${T.border}`, padding: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <img src={shopLogo} alt={shopName} style={{ width: "100%", height: "100%", objectFit: "cover" }} onError={e => { (e.currentTarget as HTMLImageElement).src = appLogo; }} />
        </button>
        <span style={{ fontWeight: 900, fontSize: 14, color: T.black, textDecoration: "underline", textDecorationColor: T.orange, textDecorationThickness: 2, textUnderlineOffset: 4 }}>{shopName}</span>
        <div style={{ flex: 1 }} />
        <motion.button whileHover={{ scale: 1.08 }} whileTap={{ scale: 0.92 }} onClick={() => goToBrand(brandId)}
          style={{ width: 30, height: 30, borderRadius: 8, border: "none", background: "#fff", boxShadow: `0 3px 10px ${RGBA_DARK(0.14)}`, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", flexShrink: 0 }}>
          <ChevronRight size={16} color={T.red} />
        </motion.button>
      </div>
    );
  };

  /* ═══════════════════════════════════
     PRODUCT CARD (unchanged UI)
  ═══════════════════════════════════ */
  const ProductCard = ({ item, isMob }: { item: any; isMob: boolean }) => {
    const q         = getQty(item.listingId);
    const price     = `${symbol ?? "€"}${((item.rate ?? 0) * (rate ?? 1)).toFixed(2)}`;
    const discount  = item.discount ?? (item.compareAtRate && item.compareAtRate > (item.rate ?? 0)
      ? Math.round(((item.compareAtRate - (item.rate ?? 0)) / item.compareAtRate) * 100) : null);
    const summary   = ratingSummaries[item.listingId];
    const hasRating = summary && summary.totalReviews > 0;
    const accent    = productAccentColor(item.listingId ?? item.name ?? "x");
    const isWished  = wished.has(item.listingId);

    const overlap  = isMob ? 58 : 100;
    const imgZoneH = isMob ? 200 : 300;

    return (
      <motion.div className="p-card" whileTap={{ scale: 0.96 }}
        style={{ borderRadius: 16, overflow: "visible", background: "#fff", boxShadow: `0 1px 2px ${RGBA_DARK(0.05)}, 0 10px 28px ${RGBA_DARK(0.08)}`, cursor: "pointer", display: "flex", flexDirection: "column", height: "100%", marginTop: overlap }}
        onClick={() => goToDetails(item.listingId)}>
        <div style={{ width: "100%", height: imgZoneH, marginTop: -overlap, position: "relative", zIndex: 4, overflow: "visible", display: "flex", alignItems: "center", justifyContent: "center" }}>
          {item.photos?.[0] ? (
            <div style={{ width: "115%", height: "115%", transform: "translateY(-5%)" }}>
              <motion.img src={item.photos[0]} alt={item.name} whileHover={{ scale: 1.06 }} transition={{ type: "spring", stiffness: 300, damping: 24 }}
                style={{ width: "100%", height: "100%", objectFit: "contain", objectPosition: "bottom", display: "block", filter: `drop-shadow(0 16px 20px ${RGBA_DARK(0.25)})` }} />
            </div>
          ) : (
            <div style={{ width: "60%", height: "70%", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Package size={isMob ? 40 : 50} color={T.orange} style={{ opacity: 0.4 }} />
            </div>
          )}

          <button onClick={e => toggleWish(item.listingId, e)}
            style={{ position: "absolute", top: overlap - 4, right: 4, background: "rgba(255,255,255,.92)", border: "none", width: 30, height: 30, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", boxShadow: `0 2px 8px ${RGBA_DARK(0.14)}`, zIndex: 6 }}>
            <Heart size={13} color={isWished ? T.red : T.muted} fill={isWished ? T.red : "none"} />
          </button>

          {discount && (
            <div style={{ position: "absolute", top: overlap + 8, left: 4, background: T.red, color: "#fff", fontSize: 9, fontWeight: 800, padding: "2px 8px", borderRadius: 6, zIndex: 5 }}>{discount}% OFF</div>
          )}
          {item.isNewArrival && !discount && (
            <div style={{ position: "absolute", top: overlap + 8, left: 4, background: T.blue, color: "#fff", fontSize: 9, fontWeight: 800, padding: "2px 8px", borderRadius: 6, zIndex: 5 }}>NEW</div>
          )}
          {hasRating && (
            <div style={{ position: "absolute", top: overlap + (discount || item.isNewArrival ? 30 : 8), left: 4, zIndex: 6, background: "rgba(255,255,255,.92)", border: `1px solid ${T.border}`, borderRadius: 8, padding: isMob ? "3px 7px" : "4px 9px", display: "flex", alignItems: "center", gap: 3 }}>
              <Star size={isMob ? 9 : 10} fill={T.orange} color={T.orange} />
              <span style={{ color: T.black, fontWeight: 800, fontSize: isMob ? 9 : 10 }}>{summary!.avgRating.toFixed(1)}</span>
              <span style={{ color: T.muted, fontSize: isMob ? 8 : 9 }}>({summary!.totalReviews.toLocaleString()})</span>
            </div>
          )}
        </div>

        <div style={{ padding: isMob ? "2px 12px 12px" : "0px 16px 16px", display: "flex", flexDirection: "column", gap: isMob ? 2 : 3, flex: 1, justifyContent: "flex-end" }}>
          <div style={{ fontWeight: 800, fontSize: isMob ? 13 : 15, color: T.black, lineHeight: 1.25, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" as any, overflow: "hidden", minHeight: isMob ? 19 : 24 }}>
            {item.name}
          </div>
          <span style={{
            display: "inline-flex", alignItems: "center", alignSelf: "flex-start",
            background: T.gradH, backgroundSize: "200% 100%",
            animation: "gradSpin 3s ease infinite", borderRadius: 8, padding: "3px 12px",
            fontWeight: 900, color: "#fff", letterSpacing: "-0.4px",
            boxShadow: `0 3px 12px ${RGBA_RED(0.32)}`, fontSize: isMob ? 14 : 17,
          }}>{price}</span>

          {q > 0 ? (
            <motion.div initial={{ scale: 0.92, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} onClick={e => e.stopPropagation()} className="add-btn-3d"
              style={{ width: "100%", height: isMob ? 40 : 46, boxSizing: "border-box", display: "flex", alignItems: "center", justifyContent: "space-between", background: "#fff", border: `2px solid ${accent}`, borderRadius: 10, padding: "0 6px", ["--shadow" as any]: `${accent}45`, boxShadow: `0 4px 14px var(--shadow)`, marginTop: isMob ? 2 : 4 }}>
              <button style={{ width: isMob ? 26 : 30, height: isMob ? 26 : 30, borderRadius: 6, border: "none", background: accent, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}
                onClick={e => { e.stopPropagation(); dispatch(decrementCartItem({ listingId: item.listingId })); }}>
                <Minus size={isMob ? 12 : 14} color="#fff" />
              </button>
              <span style={{ color: accent, fontWeight: 900, fontSize: isMob ? 13 : 15 }}>{q}</span>
              <button style={{ width: isMob ? 26 : 30, height: isMob ? 26 : 30, borderRadius: 6, border: "none", background: accent, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}
                onClick={e => { e.stopPropagation(); handleAddToCart(item); }}>
                <Plus size={isMob ? 12 : 14} color="#fff" />
              </button>
            </motion.div>
          ) : (
            <motion.button className="add-btn-3d" whileTap={{ scale: 0.96 }}
              style={{ width: "100%", height: isMob ? 40 : 46, boxSizing: "border-box", borderRadius: 10, marginTop: isMob ? 2 : 4, border: `2px solid ${accent}`, padding: 0, display: "flex", alignItems: "center", justifyContent: "center", gap: 7, background: "#fff", ["--shadow" as any]: `${accent}45`, boxShadow: `0 4px 14px var(--shadow)`, cursor: "pointer" }}
              onClick={e => { e.stopPropagation(); handleAddToCart(item); }}>
              <ShoppingCart size={isMob ? 13 : 15} color={accent} />
              <span style={{ color: accent, fontWeight: 900, fontSize: isMob ? 11 : 13, letterSpacing: "0.06em" }}>ADD TO CART</span>
            </motion.button>
          )}
        </div>
      </motion.div>
    );
  };

  /* ═══════════════════════════════════
     SORRY / NO SHOPS FOUND
  ═══════════════════════════════════ */
  const SorryNoStores = ({ categoryName }: { categoryName: string }) => (
    <div style={{ textAlign: "center", padding: 56 }}>
      <div style={{ fontSize: 40, marginBottom: 14 }}>⛽</div>
      <h3 style={{ fontWeight: 900, fontSize: 22, color: T.black, marginBottom: 10 }}>Sorry, no {categoryName} shops found near you</h3>
      <p style={{ color: T.muted, marginBottom: 24, fontSize: 13 }}>Try another category, or switch to global mode.</p>
      <button onClick={resetAllFilters} style={{ padding: "12px 28px", borderRadius: 10, border: "none", background: GRAD_H, color: "#fff", fontWeight: 800, cursor: "pointer", fontSize: 13 }}>View All Categories</button>
    </div>
  );

  /* ═══════════════════════════════════
     PRODUCT ROWS — grouped by brand, standard grid.
  ═══════════════════════════════════ */
  const renderProducts = (isMob: boolean) => (
    <div style={{ marginBottom: 28 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 18 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div style={{ width: 4, height: 22, borderRadius: 2, background: GRAD_H }} />
          <span style={{ fontWeight: 900, fontSize: 15, color: T.black }}>
            {activeCatName ?? (locationDenied ? "All Products" : showGlobal ? "Global" : userCity ? `Near ${userCity}` : userCountry ?? "All Products")}
          </span>
          <span style={{ fontSize: 12, color: T.muted, background: RGBA_DARK(0.06), padding: "2px 9px", borderRadius: 8 }}>{filteredProducts.length}</span>
        </div>
        {locationActive && (
          <button onClick={handleToggleGlobal} style={{ display: "flex", alignItems: "center", gap: 5, background: showGlobal ? "#fff" : RGBA_DARK(0.06), borderRadius: 8, padding: "5px 13px", border: `1.5px solid ${showGlobal ? T.border : RGBA_RED(0.3)}`, fontSize: 11, fontWeight: 700, color: showGlobal ? T.mutedDim : T.red, cursor: "pointer" }}>
            {showGlobal ? <MapPin size={11} /> : <Globe size={11} />}{showGlobal ? "Show Local" : "Show Global"}
          </button>
        )}
      </div>

      {activeCatName && productsByBrand.length === 0 ? (
        <SorryNoStores categoryName={activeCatName} />
      ) : filteredProducts.length > 0 ? (
        productsByBrand.map(group => (
          <div key={group.brandId}>
            <BrandRowHeader brandId={group.brandId} />
            <div style={isMob ? S.mobileGrid : S.grid}>
              {group.items.map((item: any) => <ProductCard key={item.listingId} item={item} isMob={isMob} />)}
            </div>
          </div>
        ))
      ) : (
        <div style={{ textAlign: "center", padding: 56 }}>
          <div style={{ fontSize: 40, marginBottom: 14 }}>⛽</div>
          <h3 style={{ fontWeight: 900, color: T.black, marginBottom: 8 }}>{locationDenied ? "Location needed" : "No parts found"}</h3>
          <p style={{ color: T.muted, marginBottom: 24, fontSize: 13 }}>{locationDenied ? "Enable location to see local shops." : "No products match your current filters."}</p>
          <button onClick={() => locationDenied ? setLocationModalOpen(true) : resetAllFilters()}
            style={{ padding: "12px 28px", borderRadius: 10, border: "none", background: GRAD_H, color: "#fff", fontWeight: 800, cursor: "pointer", fontSize: 13 }}>
            {locationDenied ? "Enable Location" : "View All Products"}
          </button>
        </div>
      )}
    </div>
  );

  /* ═══════════════════════════════════
     VARIANT MODAL
  ═══════════════════════════════════ */
  const VariantModal = () => {
    if (!variantOpen || !variantItem) return null;
    const variantColorIds = Object.keys(variantItem?.variants ?? {}).map(Number);
    const sizeIds: number[] = selColor && variantItem.variants?.[selColor]?.sizeIds ? variantItem.variants[selColor].sizeIds : [];
    const previewImg = selColor && variantItem?.variants?.[selColor]?.imageUrl ? variantItem.variants[selColor].imageUrl : variantItem?.photos?.[0];

    const content = (
      <div style={{ background: "#fff", borderRadius: isMobile ? "20px 20px 0 0" : 18, overflow: "hidden", maxWidth: isMobile ? "100%" : 460, width: "100%", maxHeight: isMobile ? "85vh" : "80vh", display: "flex", flexDirection: "column" }}>
        <div style={{ height: 4, background: GRAD_H, backgroundSize: "200% 100%", animation: "gradSpin 4s ease infinite", flexShrink: 0 }} />
        {isMobile && <div style={{ width: 36, height: 4, borderRadius: 2, background: RGBA_DARK(0.18), margin: "12px auto 0", flexShrink: 0 }} />}

        <div style={{ overflowY: "auto", padding: "20px 20px 0" }}>
          {previewImg && (
            <div style={{ width: "100%", aspectRatio: "16/7", borderRadius: 12, overflow: "hidden", marginBottom: 16, background: T.cream }}>
              <img src={previewImg} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
            </div>
          )}
          <div style={{ fontSize: 15, fontWeight: 800, color: T.black, marginBottom: 4 }}>{variantItem?.name}</div>
          <div style={{ fontSize: 20, fontWeight: 900, background: GRAD_H, backgroundSize: "200% 100%", animation: "gradSpin 3s ease infinite", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", marginBottom: 18 }}>
            {symbol ?? "€"}{((variantItem?.rate ?? 0) * (rate ?? 1)).toFixed(2)}
          </div>

          {variantColorIds.length > 0 && (
            <>
              <div className="gauge-label" style={{ fontSize: 10, fontWeight: 800, color: T.mutedDim, marginBottom: 12 }}>
                COLOUR
                {selColor && (() => {
                  const cv = colorPalette.find(c => c.id === selColor);
                  return cv ? <span style={{ fontWeight: 600, color: T.red, letterSpacing: "0.06em", textTransform: "none", fontSize: 12 }}> — {cv.name}</span> : null;
                })()}
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(64px,1fr))", gap: 10, marginBottom: 20 }}>
                {variantColorIds.map(cId => {
                  const cv = colorPalette.find(c => c.id === cId);
                  const hex = (cv as any)?.hex ?? (cv as any)?.hexCodes?.[0];
                  const isSel = selColor === cId;
                  return (
                    <button key={cId} onClick={() => { setSelColor(cId); setSelSize(null); }} style={{ background: "none", border: "none", padding: 0, cursor: "pointer", display: "flex", flexDirection: "column", alignItems: "center", gap: 5 }}>
                      <div style={{ position: "relative", width: 40, height: 40 }}>
                        <div style={{
                          width: 40, height: 40, borderRadius: "50%",
                          background: hex ? sphere3D(hex) : sphere3D(T.orange),
                          boxShadow: `inset -6px -5px 12px ${RGBA_DARK(0.30)},inset 5px 5px 10px rgba(255,255,255,.22),0 5px 18px ${RGBA_DARK(0.16)}${isSel ? ",0 0 0 2.5px " + T.red : ""}`,
                          outline: isSel ? `2.5px solid ${T.red}` : "none", outlineOffset: 3,
                        }} />
                        {isSel && (
                          <div style={{ position: "absolute", bottom: -3, right: -3, width: 16, height: 16, borderRadius: "50%", background: T.red, display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 1px 4px rgba(0,0,0,.3)" }}>
                            <span style={{ color: "#fff", fontSize: 9, fontWeight: 900 }}>✓</span>
                          </div>
                        )}
                      </div>
                      <span style={{ fontSize: 9, fontWeight: isSel ? 700 : 500, color: isSel ? T.red : T.mutedDim, textAlign: "center", maxWidth: 60, wordBreak: "break-word" as const, lineHeight: 1.2 }}>{cv?.name ?? String(cId)}</span>
                    </button>
                  );
                })}
              </div>
            </>
          )}

          {selColor && sizeIds.length > 0 && (
            <>
              <div className="gauge-label" style={{ fontSize: 10, fontWeight: 800, color: T.mutedDim, marginBottom: 12 }}>SIZE</div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 20 }}>
                {sizeIds.map((sId: number) => {
                  const szv = sizePalette.find(s => s.id === sId);
                  const isSel = selSize === sId;
                  return (
                    <button key={sId} onClick={() => setSelSize(sId)}
                      style={{ padding: "8px 16px", border: `1.5px solid ${isSel ? T.red : T.border}`, background: isSel ? T.red : "#fff", color: isSel ? "#fff" : T.black, fontSize: 11, fontWeight: 700, cursor: "pointer", borderRadius: 8, transition: "all .15s" }}>
                      {szv?.name ?? sId}
                    </button>
                  );
                })}
              </div>
            </>
          )}

          <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 20 }}>
            <span className="gauge-label" style={{ fontSize: 10, fontWeight: 800, color: T.mutedDim }}>QTY</span>
            <div style={{ display: "flex", border: `1.5px solid ${T.border}`, borderRadius: 10, overflow: "hidden", background: T.pageBg }}>
              <button onClick={() => setQty(v => Math.max(1, v - 1))} style={{ width: 40, height: 40, border: "none", background: "transparent", cursor: "pointer", fontSize: 16, color: T.red }}>−</button>
              <div style={{ width: 44, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 15, fontWeight: 800, color: T.black, borderLeft: `1px solid ${T.border}`, borderRight: `1px solid ${T.border}` }}>{qty}</div>
              <button onClick={() => setQty(v => v + 1)} style={{ width: 40, height: 40, border: "none", background: "transparent", cursor: "pointer", fontSize: 16, color: T.red }}>+</button>
            </div>
          </div>
        </div>

        <div style={{ padding: "14px 20px 20px", display: "flex", gap: 10, flexShrink: 0, borderTop: `1px solid ${T.border}` }}>
          <button disabled={!canContinue} onClick={() => handleVariantContinue(false)}
            style={{ flex: 1, padding: "13px 0", borderRadius: 10, border: `1.5px solid ${T.black}`, background: "transparent", color: T.black, fontWeight: 700, fontSize: 13, cursor: canContinue ? "pointer" : "not-allowed", opacity: canContinue ? 1 : 0.45 }}>
            Add to Cart
          </button>
          <button disabled={!canContinue} onClick={() => handleVariantContinue(true)}
            style={{ flex: 1, padding: "13px 0", borderRadius: 10, border: "none", background: canContinue ? GRAD_H : "#D8D5CE", color: "#fff", fontWeight: 800, fontSize: 13, cursor: canContinue ? "pointer" : "not-allowed" }}>
            Buy Now
          </button>
        </div>
      </div>
    );

    return (
      <AnimatePresence>
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          onClick={() => { setVariantOpen(false); setVariantItem(null); setSelColor(null); }}
          style={{ position: "fixed", inset: 0, background: RGBA_DARK(0.55), zIndex: 500, backdropFilter: "blur(6px)" }}>
          {isMobile ? (
            <motion.div initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }} transition={{ type: "spring", stiffness: 340, damping: 30 }}
              onClick={e => e.stopPropagation()} style={{ position: "fixed", bottom: 0, left: 0, right: 0, zIndex: 501 }}>{content}</motion.div>
          ) : (
            <div style={{ position: "fixed", inset: 0, zIndex: 501, display: "flex", alignItems: "center", justifyContent: "center" }} onClick={e => e.stopPropagation()}>
              <motion.div initial={{ scale: 0.95, opacity: 0, y: 10 }} animate={{ scale: 1, opacity: 1, y: 0 }} exit={{ scale: 0.95, opacity: 0, y: 10 }}
                transition={{ type: "spring", damping: 26, stiffness: 340 }} style={{ width: 460, borderRadius: 18, overflow: "hidden", boxShadow: `0 32px 80px ${RGBA_DARK(0.35)}` }}>
                {content}
              </motion.div>
            </div>
          )}
        </motion.div>
      </AnimatePresence>
    );
  };

  /* ═══════════════════════════════════
     LOCATION MODAL
  ═══════════════════════════════════ */
  const LocationModal = () => (
    <AnimatePresence>
      {locationModalOpen && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setLocationModalOpen(false)}
          style={{ position: "fixed", inset: 0, background: RGBA_DARK(0.6), zIndex: 2000, display: "flex", alignItems: "center", justifyContent: "center", backdropFilter: "blur(8px)" }}>
          <motion.div initial={{ scale: 0.9, y: 40 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.9, y: 40 }} onClick={e => e.stopPropagation()}
            style={{ width: 320, borderRadius: 16, padding: 24, background: "#fff", boxShadow: `0 30px 80px ${RGBA_DARK(0.32)}`, textAlign: "center" }}>
            <div style={{ height: 5, borderRadius: 6, background: GRAD_H, marginBottom: 20 }} />
            <div style={{ fontSize: 32, marginBottom: 10 }}>📍</div>
            <h3 style={{ fontWeight: 900, marginBottom: 8, color: T.black }}>Location Disabled</h3>
            <p style={{ color: T.muted, margin: "0 0 22px", fontSize: 13, lineHeight: 1.6 }}>Enable location in your browser settings to see local auto shops.</p>
            <button onClick={() => setLocationModalOpen(false)} style={{ padding: "11px 28px", borderRadius: 10, border: "none", background: GRAD_H, color: "#fff", fontWeight: 700, cursor: "pointer", fontSize: 12 }}>Got it</button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  /* ═══════════════════════════════════
     MOBILE BOTTOM TABS
  ═══════════════════════════════════ */
  const BottomTabs = () => isMobile && userId ? (
    <div style={S.mobileTabs}>
      {[
        { icon: <Home size={22} />, label: "Home", active: true, action: () => navigate(`/AutomotiveHome/${userId}`) },
        { icon: <Wrench size={22} />, label: "Shops", active: false, action: () => navigate("/Stores") },
        { label: "Cart", active: false, icon: (
            <div style={{ position: "relative" }}>
              <ShoppingCart size={22} />
              {cartItems.length > 0 && <span style={{ position: "absolute", top: -5, right: -5, background: T.red, color: "#fff", fontSize: 8, fontWeight: 900, width: 16, height: 16, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center" }}>{cartItems.reduce((s: number, i: any) => s + i.qty, 0)}</span>}
            </div>
          ), action: goToCheckout },
        { icon: <ShoppingBag size={22} />, label: "Orders", active: false, action: () => navigate(`/autoclientdashboard/${userId}`) },
        { icon: <User size={22} />, label: "Profile", active: false, action: () => navigate(`/profile/${userId}`) },
      ].map(t => (
        <motion.div key={t.label} whileTap={{ scale: 0.88 } as any} onClick={t.action} className={`tab-item${t.active ? " active" : ""}`} style={{ position: "relative" }}>
          {React.cloneElement(t.icon as any, { color: t.active ? T.red : "#9AA3AD" })}
          <span>{t.label}</span>
        </motion.div>
      ))}
    </div>
  ) : null;

  /* ═══════════════════════════════════
     RENDER
  ═══════════════════════════════════ */
  return (
    <div style={S.page}>
      <style>{GLOBAL_CSS}</style>
      <PageWatermark />

      <div style={S.content}>
        <div style={S.headerTop}>
          <HeaderRow />
        </div>

        <MobileCategorySheet />

        {!isMobile && (
          <div style={{ ...S.desktopGrid, gridTemplateColumns: desktopCatOpen ? "300px 1fr" : "1fr" }}>
            {desktopCatOpen && (
              <aside style={A.sidebar}>
                <CategoryPanelContent />
              </aside>
            )}
            <main style={{ minWidth: 0 }}>
              <CategoryIconBar />
              <ShopByCategory />

              {activeCat != null && (
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                  style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16, padding: "10px 14px", background: RGBA_RED(0.08), borderRadius: 10, border: `1.5px solid ${T.red}` }}>
                  <Tag size={16} color={T.red} />
                  <span style={{ fontSize: 12.5, fontWeight: 700, color: T.red, flex: 1 }}>Browsing: {activeCatName}</span>
                  <button onClick={() => { setActiveCat(null); setActiveProductGroup(null); }} style={{ background: "#fff", border: "none", borderRadius: 6, width: 24, height: 24, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <X size={12} color={T.red} />
                  </button>
                </motion.div>
              )}

              {renderProducts(false)}
            </main>
          </div>
        )}

        {isMobile && (
          <div style={{ padding: "14px 12px 0" }}>
            <CategoryIconBar />
            <div style={{ marginTop: 16 }}><ShopByCategory /></div>

            {activeCat != null && (
              <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16, padding: "10px 14px", background: RGBA_RED(0.08), borderRadius: 10, border: `1.5px solid ${T.red}` }}>
                <Tag size={16} color={T.red} />
                <span style={{ fontSize: 12.5, fontWeight: 700, color: T.red, flex: 1 }}>Browsing: {activeCatName}</span>
                <button onClick={() => { setActiveCat(null); setActiveProductGroup(null); }} style={{ background: "#fff", border: "none", borderRadius: 6, width: 24, height: 24, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <X size={12} color={T.red} />
                </button>
              </motion.div>
            )}

            {renderProducts(true)}
          </div>
        )}

        <VariantModal />
        <LocationModal />

        <BottomTabs />
      </div>
    </div>
  );
};

export default AutomotiveHome;